import { DatePipe, SlicePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import {
  CardComponent,
  EmptyStateComponent,
  IconComponent,
  modalFn,
  MultilineInputComponent,
  TranslatePipe,
  AccordionComponent,
  AccordionItemComponent,
  AccordionItemHeaderComponent,
  AccordionItemLeadingComponent,
  AccordionItemTitleComponent,
  AccordionItemContentComponent,
} from '@softline/ui-core';
import {
  TimelineComponent,
  TimelineItemComponent,
  TimelineTitleComponent,
  TimelineMetaComponent,
} from '@softline/ui-core/timeline';
import { WorkflowService } from '../../services/workflow.service';
import {
  WorkflowParticipant,
  WorkflowParticipants,
  WorkflowRole,
} from '../../types/workflow';
import { WorkflowParticipantsModal } from '../workflow-participants-modal/workflow-participants-modal';
import { WorkflowParticipants as WorkflowParticipantsComponent } from '../workflow-participants/workflow-participants';

@Component({
  selector: 'soft-workflow-details-tab',
  imports: [
    IconComponent,
    DatePipe,
    SlicePipe,
    MultilineInputComponent,
    TranslatePipe,
    CardComponent,
    EmptyStateComponent,
    WorkflowParticipantsComponent,
    AccordionComponent,
    AccordionItemComponent,
    AccordionItemHeaderComponent,
    AccordionItemLeadingComponent,
    AccordionItemTitleComponent,
    AccordionItemContentComponent,
    TimelineComponent,
    TimelineItemComponent,
    TimelineTitleComponent,
    TimelineMetaComponent,
  ],
  templateUrl: './workflow-details-tab.component.html',
  styleUrl: './workflow-details-tab.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkflowDetailsTabComponent {
  private readonly workflowService = inject(WorkflowService);
  private readonly modal = modalFn();

  public readonly workflow = this.workflowService.value;
  public readonly history = this.workflowService.history;
  public readonly comments = this.workflowService.comments;

  public readonly newCommentText = signal<string | undefined | null>('');

  public readonly removeParticipant = async (
    participant: WorkflowParticipant & { role: string }
  ) => {
    const participants = this.workflow()?.currentAction?.participants;
    const workflowId = this.workflow()?.id;
    if (!participants || !workflowId) return;

    const payload = this.buildRemoveParticipantsPayload(participant, participants);
    await this.workflowService.updateParticipants(workflowId, payload);
  };

  private buildRemoveParticipantsPayload(
    participant: WorkflowParticipant & { role: string },
    participants: WorkflowParticipants
  ): WorkflowParticipants {
    const { availableParticipants, availableRoles, currentRoles, currentParticipants } =
      participants;

    const updatedCurrentRoles = currentRoles
      .map((role) => {
        if (role.role !== participant.role) return role;
        return {
          ...role,
          participants: role.participants.filter((p) => p.id !== participant.id),
        };
      })
      .filter((role) => role.participants.length > 0);

    const participantIds = new Set(
      updatedCurrentRoles.flatMap((r) => r.participants.map((p) => p.id))
    );
    const updatedCurrentParticipants = currentParticipants.filter((p) =>
      participantIds.has(p.id)
    );

    return {
      availableParticipants,
      availableRoles,
      currentParticipants: updatedCurrentParticipants,
      currentRoles: updatedCurrentRoles,
    };
  }

  public readonly onAddParticipant = async (role: {
    role: WorkflowRole;
    isGroup: boolean;
  }) => {
    const participants = this.workflow()?.currentAction?.participants;
    const workflowId = this.workflow()?.id;
    if (!participants || !workflowId) return;

    const filteredParticipants = participants.availableParticipants.filter(
      (p) => p.isGroup === role.isGroup
    );
    const title = role.isGroup ? 'Gruppen' : 'Benutzer';

    const result = await this.modal({
      component: WorkflowParticipantsModal,
      dismiss: true,
      title,
      data: {
        role: role.role,
        participants: filteredParticipants,
        title,
      },
    });

    if (result === 'DISMISSED' || result === null) return;

    const payload = this.buildAddParticipantsPayload(role.role, result, participants);
    await this.workflowService.updateParticipants(workflowId, payload);
  };

  private buildAddParticipantsPayload(
    role: WorkflowRole,
    selected: WorkflowParticipant[],
    participants: WorkflowParticipants
  ): WorkflowParticipants {
    const { availableParticipants, availableRoles, currentRoles } = participants;

    const updatedCurrentRoles = [
      ...currentRoles.filter((r) => r.role !== role.role),
      ...(selected.length ? [{ ...role, participants: selected }] : []),
    ];

    const currentParticipants = updatedCurrentRoles
      .flatMap((r) => r.participants)
      .filter((p, i, arr) => arr.findIndex((x) => x.id === p.id) === i);

    return {
      availableParticipants,
      availableRoles,
      currentParticipants,
      currentRoles: updatedCurrentRoles,
    };
  }

  public readonly addComment = async () => {
    const text = this.newCommentText()?.trim();
    const workflowId = this.workflow()?.id;
    if (text && workflowId) {
      await this.workflowService.addComment(workflowId, text);
      this.newCommentText.set('');
    }
  };

  public readonly deleteComment = async (commentId: number | string) => {
    await this.workflowService.deleteComment(commentId);
  };
}
